import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Map as MapIcon } from 'lucide-react';
import { ParkingArea } from '../../types/parking';
import { BuildingStructure } from '../../types/protocols';
import { translateParkingAreaKind } from '../../types/EnumTranslations';
import { useFirebaseImage } from '../../Helpers/ImageLoader';
import { AREA_BLUEPRINT_STATUS_TEXT, isSpotRented, parkingAreaDisplayName, resolveAreaBlueprint, spotHasCharger, vacantSpotCount } from '../../Helpers/parkingUtils';
import Skeleton from '../../Components/Skeleton';

interface ParkingAreaCardProps {
    area: ParkingArea;
    /** Rooms and levels, needed only when the area uses its level's drawing. */
    structure?: Pick<BuildingStructure, 'rooms' | 'levels'>;
    /** The area page the card links to. */
    to: string;
}

/**
 * The box the drawing is drawn in inside a container, with the image's aspect
 * ratio kept (object-fit: contain, computed so pins can be laid over it).
 */
const useContainedBox = (ratio: number | undefined) => {
    const ref = useRef<HTMLDivElement>(null);
    const [box, setBox] = useState<{ width: number; height: number } | null>(null);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el || !ratio) return;
        const measure = () => {
            const cw = el.clientWidth;
            const ch = el.clientHeight;
            if (cw / ch > ratio) setBox({ width: ch * ratio, height: ch });
            else setBox({ width: cw, height: cw / ratio });
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [ratio]);
    return { ref, box };
};

/** A parking area as a card with its drawing on top, every placed spot as a pin, and name, kind and spot counts below. */
const ParkingAreaCard: React.FC<ParkingAreaCardProps> = ({ area, structure, to }) => {
    const resolution = resolveAreaBlueprint(area, structure);
    const image = resolution.status === 'ok' ? resolution.image : undefined;
    const drawingKey = resolution.status === 'ok' ? resolution.key : undefined;
    const { src, isLoading } = useFirebaseImage(image?.storagePath, '');
    const { ref: drawingRef, box } = useContainedBox(image && image.width > 0 && image.height > 0 ? image.width / image.height : undefined);
    const spots = Object.values(area.spots || {});
    const chargers = spots.filter(spotHasCharger).length;
    const facts = [`${spots.length} platser`, `${vacantSpotCount(area)} lediga`, ...(chargers > 0 ? [`${chargers} laddare`] : [])];
    const drawingNote = resolution.status === 'ok'
        ? (resolution.owner === 'level' ? 'Våningens ritning' : null)
        : AREA_BLUEPRINT_STATUS_TEXT[resolution.status];

    return (
        <Link to={to} className="parking-area-card">
            <div className="parking-area-card-drawing" ref={drawingRef}>
                {image ? (
                    isLoading || !src
                        ? <Skeleton width="100%" height="100%" />
                        : (
                            <div className="parking-area-card-drawn" style={box ? { width: box.width, height: box.height } : undefined}>
                                <img src={src} alt={`Ritning: ${parkingAreaDisplayName(area)}`} />
                                {box && drawingKey && spots.map(spot => {
                                    const location = spot.blueprintLocations?.[drawingKey];
                                    if (!location || !(location.width > 0) || !(location.height > 0)) return null;
                                    const rented = isSpotRented(spot);
                                    return (
                                        <span
                                            key={spot.key}
                                            className={`parking-area-card-pin ${rented ? 'rented' : 'vacant'}`}
                                            style={{ left: `${(location.x / location.width) * 100}%`, top: `${(location.y / location.height) * 100}%` }}
                                            title={`Plats ${spot.number} · ${rented ? 'Uthyrd' : 'Ledig'}`}
                                        />
                                    );
                                })}
                            </div>
                        )
                ) : (
                    <div className="parking-area-card-no-drawing">
                        <MapIcon size={28} />
                        <span>{drawingNote}</span>
                    </div>
                )}
            </div>
            <div className="parking-area-card-body">
                <div className="parking-area-card-title">{parkingAreaDisplayName(area)}</div>
                <div className="parking-area-card-facts">{translateParkingAreaKind(area.kind)} · {facts.join(' · ')}</div>
                {image && drawingNote && <div className="parking-area-card-note">{drawingNote}</div>}
            </div>
        </Link>
    );
};

export default ParkingAreaCard;
